import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Store, Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublicLoginPage = () => {
  const { t } = useI18n();
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  useEffect(() => {
    loadShop();
    // Check if already logged in
    const token = localStorage.getItem(`b2c_token_${shopSlug}`);
    if (token) {
      navigate(`/shop/${shopSlug}`);
    }
  }, [shopSlug]);

  const loadShop = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/shop/${shopSlug}`);
      if (!res.ok) {
        navigate('/');
        return;
      }
      const data = await res.json();
      setShop(data);
    } catch (error) {
      console.error('Failed to load shop', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error(t('fillAll'));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/shop/${shopSlug}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Anmeldung fehlgeschlagen');
      }

      const data = await res.json();
      
      // Store token in localStorage (shop-specific)
      localStorage.setItem(`b2c_token_${shopSlug}`, data.token);
      localStorage.setItem(`b2c_customer_${shopSlug}`, JSON.stringify(data.customer));
      
      toast.success(t('plLoginSuccess'));
      navigate(`/shop/${shopSlug}`);
    } catch (error) {
      toast.error(error.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Store className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Shop nicht gefunden</h1>
          <p className="text-gray-500 mb-4">Der gesuchte Shop existiert nicht oder ist nicht aktiv.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            {t('toHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-login-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(`/shop/${shopSlug}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-black"
            >
              <ArrowLeft className="w-5 h-5" />
              {t('back')}
            </button>
            <div className="flex items-center gap-2">
              {shop.logo_url ? (
                <img 
                  src={`${API_URL}/api/files/${shop.logo_url}`}
                  alt={shop.shop_name}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <Store className="w-6 h-6 text-gray-600" />
              )}
              <span className="font-bold">{shop.shop_name}</span>
            </div>
            <div className="w-16"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm p-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2">{t('plWelcomeBack')}</h1>
            <p className="text-gray-500">
              Melden Sie sich bei {shop.shop_name} an
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                E-Mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder="max@beispiel.de"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-12 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-black"
                  placeholder={t('plYourPassword')}
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting}
              className={`w-full py-4 rounded-xl font-medium text-white transition-all ${
                submitting ? 'bg-gray-400' : 'bg-black hover:bg-gray-800'
              }`}
              data-testid="login-submit-btn"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('plSigningIn')}
                </span>
              ) : (
                t('psLogin')
              )}
            </button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Noch kein Konto?{' '}
              <Link 
                to={`/shop/${shopSlug}/register`}
                className="text-black font-medium hover:underline"
                data-testid="register-link"
              >
                {t('plRegisterNow')}
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PublicLoginPage;
